import React, { useMemo, useState } from "react"
import { Box, ButtonBase, InputAdornment, TextField, Typography } from "@mui/material"
import { FlagsIconSeeker, IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"
import AppPillToggle from "@components/@base/PillToggle"
import AppRetailerBlock from "@components/DataBlocks/Retailer"
import AppIdentityChip from "./IdentityChip"
import { AddPicker, boxSx, chipsSx, fieldLabelSx, fieldNoteSx, helperSx } from "./BrandsField"
import { LONG_LIST, NO_COUNTRY, addId, countryCount, groupByCountry, marketName, matchesQuery, preview, removeId, sortSites } from "./@services/identity"


const linkSx = {
	px: 1, height: 28, borderRadius: '6px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', color: dsColorNew.appBlue,
	'&:hover': { backgroundColor: dsColorNew.hoverBgLight },
}

/**
 * Sites: the user's sites by name with their country (criterion 4), none of them primary — each one
 * stands for the user in its own country (CU-869fmj51h) — always in `sortSites` order. The picker groups
 * by country and searches by name, country code or country name. Past `LONG_LIST` the field gets a
 * filter, the country count, List / By country and "+N more" (UX D6).
 */
export default function SitesField({ value, onChange, labels, options, kind, error, disabled }) {
	const [query, setQuery] = useState('')
	const [view, setView] = useState('list')
	const [expanded, setExpanded] = useState(false)

	const catalogue = useMemo(() => [...(options || [])]
		.filter(o => o.label)
		.map(o => ({ ...o, market_name: o.market ? marketName(o.market) : null }))
		.sort((a, b) => (!a.market) - (!b.market)
			|| String(a.market_name ?? '').localeCompare(String(b.market_name ?? ''))
			|| String(a.label).localeCompare(String(b.label))), [options])

	const items = sortSites(value.map(id => ({ id, ...(labels.sites.get(id) || { label: null, market: null, market_name: null }) })))
	const long = items.length > LONG_LIST
	const filtered = long && query ? items.filter(s => matchesQuery(s, query)) : items
	const { countries, noCountry } = countryCount(items)

	const chip = (s) => <AppIdentityChip
		key={s.id}
		id={s.id}
		kind="site"
		label={s.label}
		market={s.market}
		marketName={s.market_name}
		disabled={disabled}
		onRemove={() => onChange(removeId(value, s.id))}
	/>

	const { shown, more } = preview(filtered, expanded || !!query)

	return <Box sx={{ gridColumn: 'span 12' }}>
		<Typography component="label" sx={fieldLabelSx}>
			Sites <Box component="span" sx={fieldNoteSx}>· {value.length} · {kind === 'broker' ? 'at least one' : 'optional'}</Box>
		</Typography>
		<Box sx={boxSx(!!error)}>
			{long && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
				<TextField
					size="small"
					value={query}
					onChange={e => setQuery(e.target.value)}
					placeholder={`Filter your ${items.length} sites`}
					inputProps={{ 'aria-label': `Filter your ${items.length} sites` }}
					InputProps={{ startAdornment: <InputAdornment position="start"><IconSeeker icon="magnifying-glass" sx={{ fontSize: 12, color: dsColorNew.textSubtle }}/></InputAdornment> }}
					sx={{ width: 260, '& .MuiOutlinedInput-root': { backgroundColor: dsColorNew.white } }}
				/>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
					{countries} countr{countries === 1 ? 'y' : 'ies'}{noCountry > 0 && ` · ${noCountry} with no country`}
				</Typography>
				<AppPillToggle
					size="small"
					aria-label="Show sites as"
					value={view}
					onChange={setView}
					options={[{ value: 'list', label: 'List' }, { value: 'country', label: 'By country' }]}
					sx={{ ml: 'auto' }}
				/>
			</Box>}

			{!!filtered.length && (long && view === 'country'
				? <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(140px, max-content) 1fr', columnGap: 2, rowGap: 1.25, alignItems: 'start' }}>
					{groupByCountry(filtered).map(g => <React.Fragment key={g.market || 'none'}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 28, fontSize: 13, fontWeight: 500, color: dsColorNew.textDark }}>
							{!!g.market && <FlagsIconSeeker countryName={g.market}/>}
							{g.market_name}
							{!!g.market && <Box component="span" sx={{ fontSize: 11, fontFamily: 'monospace', color: dsColorNew.textMuted }}>{g.market}</Box>}
						</Box>
						<Box sx={chipsSx}>{g.sites.map(chip)}</Box>
					</React.Fragment>)}
				</Box>
				: <Box sx={chipsSx}>
					{shown.map(chip)}
					{more > 0 && <ButtonBase onClick={() => setExpanded(true)} title={`Show all ${filtered.length}`} sx={linkSx}>+{more} more</ButtonBase>}
					{long && expanded && !query && <ButtonBase onClick={() => setExpanded(false)} sx={linkSx}>Show less</ButtonBase>}
				</Box>)}
			{long && !!query && !filtered.length && <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>No site of yours matches “{query}”</Typography>}

			<AddPicker
				options={catalogue}
				selected={value}
				disabled={disabled}
				placeholder="Add a site — name or country…"
				emptyText="No site matches"
				groupBy={o => o.market ? `${o.market_name} · ${o.market}` : NO_COUNTRY}
				filterOptions={(opts, { inputValue }) => opts.filter(o => matchesQuery(o, inputValue))}
				renderLabel={o => <AppRetailerBlock retailer={o.label} country={o.market}/>}
				onChange={(_, o) => o && onChange(addId(value, o.value))}
			/>
		</Box>
		{error
			? <Typography role="alert" sx={{ ...helperSx, color: dsColorNew.dangerRed }}>{error}</Typography>
			: <Typography sx={helperSx}>
				<b>Each site applies to its own country.</b> A site with no country is still yours, but never stands for you in a specific country. Only sites your account follows are offered.
			</Typography>}
	</Box>
}
